import { useEffect, useState } from 'react';

import {
  NavigableAnnotationPopoverProps,
  useNavigationContext,
} from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { isCodedAnnotation } from 'services/webediErrors/annotationPresentation';
import { WebEDIAnnotation, WebEDIAnnotationCode, WebEDIError } from 'services/webediErrors/types';
import { WithRef } from 'support/types/withRef';

import { ConnectorHookError } from './presets/ConnectorHookError/ConnectorHookError';
import { ConnectorHookWarning } from './presets/ConnectorHookWarning/ConnectorHookWarning';
import { EphemeralPartnerWarning } from './presets/EphemeralPartnerWarning/EphemeralPartnerWarning';
import { ErrorsResolved } from './presets/ErrorsResolved/ErrorsResolved';
import { FieldError } from './presets/FieldError/FieldError';
import { OcrVerificationWarning } from './presets/OcrVerificationWarning/OcrVerificationWarning';
import { UnmatchedArticleWarning } from './presets/UnmatchedArticleWarning/UnmatchedArticleWarning';

type FieldAnnotationPopoverProps = NavigableAnnotationPopoverProps<WebEDIAnnotation>;

export const FieldAnnotationPopover = ({
  style,
  annotation,
  isValidating,
  isPendingValidation,
  viewMode,
  fieldDescription,
  ref,
}: WithRef<FieldAnnotationPopoverProps, HTMLDivElement>) => {
  // The popover only mounts when a cell is focused, so subscribing here means
  // only the focused cell's popover hits the nav + ack stores. Previously the
  // count flowed in as a prop from every cell wrapper in the document, fanning
  // out an ack-store subscription per cell — wasteful on large documents
  // because every cell would re-render on every "Mark as resolved" click.
  const { service } = useNavigationContext<WebEDIAnnotation>();
  const numberOfErrors = service.useNumberOfActiveItems();
  const [annotationState, setAnnotationState] = useState<WebEDIAnnotation | undefined>(annotation);

  useEffect(() => {
    if (annotation) {
      setAnnotationState(annotation);
    }
  }, [annotation]);

  if (!annotationState) {
    return null;
  }

  const isResolved = !annotation;
  const hasOpenErrors = numberOfErrors > 0;

  if (!hasOpenErrors && isResolved) {
    return <ErrorsResolved ref={ref} style={style} />;
  }

  if (isCodedAnnotation(annotationState)) {
    switch (annotationState.code) {
      case WebEDIAnnotationCode.UNMATCHED_ARTICLE:
        return <UnmatchedArticleWarning ref={ref} style={style} annotation={annotationState} viewMode={viewMode} />;
      case WebEDIAnnotationCode.CONNECTOR_HOOK:
        return annotationState.severity === 'error' ? (
          <ConnectorHookError ref={ref} style={style} annotation={annotationState} viewMode={viewMode} />
        ) : (
          <ConnectorHookWarning ref={ref} style={style} annotation={annotationState} viewMode={viewMode} />
        );
      case WebEDIAnnotationCode.EPHEMERAL_PARTNER:
        return <EphemeralPartnerWarning ref={ref} style={style} annotation={annotationState} viewMode={viewMode} />;
      case WebEDIAnnotationCode.OCR_VERIFICATION_WARNING:
        return <OcrVerificationWarning ref={ref} style={style} annotation={annotationState} viewMode={viewMode} />;
      default:
        annotationState satisfies never;
    }
  }

  return (
    <FieldError
      ref={ref}
      style={style}
      error={annotationState as WebEDIError}
      isErrorFixed={isResolved}
      isValidating={isValidating}
      isPendingValidation={isPendingValidation}
      viewMode={viewMode}
      fieldDescription={fieldDescription}
    />
  );
};
